import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import type { GoodsAction } from '@stores/goods/action';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import { deleteUserAddress, getMyPrizeWinningList, getPrizeExchangeList, getUserAddressList, setDefaultUserAddress, updateOrderAddress } from '@server';
import TabNav from '@components/tabNav';
import CommonPopupNew from '@components/commonPopupNew';
import Toast from '@components/Toast';
import { px2rem } from '@utils/px2rem';
import { getUrlParams } from '@utils/getUrlParams';
import { listenScrollDirection } from '@utils/scrollUtils';
import { formatDateTime } from '@utils/timeUtils';
import styles from './index.module.scss';
import { BASE_IMG_URL } from '@images/imageUrls';
import classNames from 'classnames';

type TabKey = 'prizes' | 'records' | 'address';

interface PrizeManagementH5Props extends RouteComponentProps {
    goods?: GoodsAction;
    site?: SiteAction;
    user?: UserAction;
    desktopMode?: boolean;
}

// 页面顶部 Tab 的业务标识和显示名称
const tabs: Array<{ key: TabKey; name: string }> = [
    { key: 'prizes', name: '我的奖品' },
    { key: 'records', name: '兑换记录' },
    { key: 'address', name: '收货地址' },
];

// 我的奖品二级分类及接口 type 参数映射。
const prizeTypeTabs: Array<{ type: number; name: string }> = [
    { type: 0, name: '全部' },
    { type: 1, name: 'V币' },
    { type: 3, name: '虚拟卡' },
    { type: 2, name: '实物' },
];

// 从路由参数中读取有效的奖品分类，未传或异常时默认全部。
const getPrizeFilterType = (search: string) => {
    const type = Number(new URLSearchParams(search).get('type'));
    return prizeTypeTabs.some(item => item.type === type) ? type : 0;
};

// 兼容不同浏览器的页面滚动位置读取方式。
const getPageScrollTop = () => Math.max(
    typeof window !== 'undefined' ? window.pageYOffset || 0 : 0,
    typeof document !== 'undefined' ? document.documentElement.scrollTop || 0 : 0,
    typeof document !== 'undefined' ? document.body.scrollTop || 0 : 0,
);

// 管理页所有列表接口统一使用每页 20 条。
const PAGE_SIZE = 20;

// 处理卡号、卡密、订单号和物流单号复制操作的按钮
class CopyButton extends React.Component<{ value: string }> {

    // 复制成功后显示 Toast 提示。
    showCopiedState = () => {
        Toast.success({ content: '复制成功' });
    };

    // 使用系统剪贴板复制文本，兼容不支持 Clipboard API 的 H5 WebView。
    copy = async () => {
        const value = String(this.props.value);
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
            } else {
                this.copyWithFallback(value);
            }
            this.showCopiedState();
        } catch (_) {
            try {
                this.copyWithFallback(value);
                this.showCopiedState();
            } catch (__) {
                // 复制失败时保持原按钮图标，不显示成功提示。
            }
        }
    };

    // 通过隐藏文本框降级复制，适配非安全上下文或旧版内置浏览器。
    copyWithFallback = (value: string) => {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) throw new Error('复制失败');
    };

    // 渲染复制按钮
    render() {
        const { value } = this.props;
        return <button type="button" className={styles.copyButton} onClick={this.copy} aria-label={`复制 ${value}`}>
            <img className={styles.copyIcon} src={`${BASE_IMG_URL}/goods/icon8.png`} alt="复制" />
        </button>;
    }
}

// 奖品没有图片时显示的手表占位图标
const WatchIcon = () => (
    <div className={styles.watchIcon} aria-hidden="true">
        <span className={styles.watchStrap} />
        <span className={styles.watchFace}><i className={styles.watchScreen} /></span>
    </div>
);

// 列表无数据时显示的空状态插图和提示文案
const EmptyState = ({ text }: { text: string }) => <div className={styles.emptyState}>
    <img className={styles.emptyIllustration} src={`${BASE_IMG_URL}/common/null.png`} alt="" />
    <p className={styles.emptyText}>{text}</p>
</div>;

// 兼容不同接口返回结构，统一提取列表数据
const getPrizeList = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    return data?.pageData || data?.list || data?.data || data?.Data || data?.rows || [];
};

// 按候选字段顺序读取接口数据中的第一个有效值
const valueOf = (item: any, ...keys: string[]) => {
    for (const key of keys) {
        if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') return item[key];
    }
    return '';
};

// 从奖品列表中读取中奖记录 ID，接口要求使用中奖记录主键更新地址。
const getWinningRecordId = (item: any) => valueOf(item, 'winningId', 'winning_id', 'recordId', 'id');

interface PrizeManagementH5State {
    activeTab: TabKey;
    prizeFilterType: number;
    prizes: any[];
    loading: boolean;
    error: string;
    prizesPage: number;
    prizesHasMore: boolean;
    prizesLoadingMore: boolean;
    records: any[];
    recordsLoading: boolean;
    recordsLoaded: boolean;
    recordsPage: number;
    recordsHasMore: boolean;
    recordsLoadingMore: boolean;
    recordsError: string;
    addresses: any[];
    addressesLoading: boolean;
    addressesLoaded: boolean;
    addressesError: string;
    addressToDelete: any;
    addressDeleting: boolean;
    addressDeleteError: string;
    settingDefaultId: string | number | null;
    selectedAddress: any;
    addressConfirming: boolean;
    addressConfirmError: string;
    scrollY: number;
    scrollDirection: 'up' | 'down' | null;
}

class PrizeManagementH5 extends React.Component<PrizeManagementH5Props, PrizeManagementH5State> {
    private mounted = false;
    private cleanupScroll?: () => void;
    private prizesRequesting = false;
    private recordsRequesting = false;

    // 初始化页面状态和默认 Tab
    constructor(props: PrizeManagementH5Props) {
        super(props);
        const searchParams = new URLSearchParams(props.location.search);
        const selectAddressMode = searchParams.get('select') === '1';
        const tab = searchParams.get('tab');
        this.state = {
            activeTab: selectAddressMode
                ? 'address'
                : tabs.some(item => item.key === tab) ? tab as TabKey : 'prizes',
            prizeFilterType: getPrizeFilterType(props.location.search),
            prizes: [],
            loading: true,
            error: '',
            prizesPage: 0,
            prizesHasMore: false,
            prizesLoadingMore: false,
            records: [],
            recordsLoading: false,
            recordsLoaded: false,
            recordsPage: 0,
            recordsHasMore: false,
            recordsLoadingMore: false,
            recordsError: '',
            addresses: [],
            addressesLoading: false,
            addressesLoaded: false,
            addressesError: '',
            addressToDelete: null,
            addressDeleting: false,
            addressDeleteError: '',
            settingDefaultId: null,
            selectedAddress: null,
            addressConfirming: false,
            addressConfirmError: '',
            scrollY: getPageScrollTop(),
            scrollDirection: null,
        };
    }

    // 初始化滚动监听、页面标题和首屏数据
    componentDidMount() {
        // 奖品管理属于登录页面，未登录时由统一登录逻辑跳转登录页。
        const platform = this.props.desktopMode ? 'pc' : 'h5';
        if (this.props.user?.checkLogin && !this.props.user.checkLogin(platform)) return;

        this.mounted = true;
        this.cleanupScroll = listenScrollDirection((scrollData: { direction: 'up' | 'down' }) => {
            this.setState({ scrollDirection: scrollData.direction });
        }) as () => void;
        window.addEventListener('scroll', this.handlePrizeListScroll);
        this.updateTitle();
        this.loadPrizes();
        this.loadActiveTabData();
    }

    // 根据路由模式或 Tab 变化补充加载对应数据
    componentDidUpdate(prevProps: PrizeManagementH5Props, prevState: PrizeManagementH5State) {
        if (prevState.activeTab !== this.state.activeTab) {
            // 各 Tab 共用页面滚动容器，切换内容后不能沿用上一个 Tab 的滚动位置。
            this.resetPageScroll();
            this.loadActiveTabData();
        }
        const selectAddressMode = this.isSelectAddressMode(this.props.location.search);
        const previousRouteTab = this.getTabFromSearch(prevProps.location.search);
        const currentRouteTab = this.getTabFromSearch(this.props.location.search);
        const previousPrizeFilterType = getPrizeFilterType(prevProps.location.search);
        const currentPrizeFilterType = getPrizeFilterType(this.props.location.search);
        if (selectAddressMode && this.state.activeTab !== 'address') {
            // 地址选择模式只展示地址列表，始终锁定地址 Tab。
            this.setState({ activeTab: 'address' });
        } else if (!selectAddressMode && currentRouteTab && currentRouteTab !== previousRouteTab && currentRouteTab !== this.state.activeTab) {
            this.setState({ activeTab: currentRouteTab });
        }
        if (
            !selectAddressMode
            && previousPrizeFilterType !== currentPrizeFilterType
            && currentPrizeFilterType !== this.state.prizeFilterType
        ) {
            // 路由分类变化时同步筛选状态并重新加载奖品列表。
            this.setState({ prizeFilterType: currentPrizeFilterType }, () => {
                this.resetPageScroll();
                this.loadPrizes();
            });
        }
        if (this.isSelectAddressMode(prevProps.location.search) !== selectAddressMode) {
            this.updateTitle();
            this.loadPrizes();
        }
        if (
            (this.state.activeTab === 'prizes' || this.state.activeTab === 'records')
            && (prevState.activeTab !== this.state.activeTab
                || prevState.prizesPage !== this.state.prizesPage
                || prevState.prizesHasMore !== this.state.prizesHasMore
                || prevState.recordsPage !== this.state.recordsPage
                || prevState.recordsHasMore !== this.state.recordsHasMore)
        ) {
            // 页面内容不足一屏时，自动检查当前列表是否需要继续加载下一页。
            this.handlePrizeListScroll();
        }
    }

    // 清理滚动监听并恢复页面标题
    componentWillUnmount() {
        this.mounted = false;
        this.cleanupScroll?.();
        window.removeEventListener('scroll', this.handlePrizeListScroll);
        document.title = '';
    }

    // 判断当前是否为选择收货地址模式
    isSelectAddressMode = (search: string) => new URLSearchParams(search).get('select') === '1';

    // 从路由参数中读取有效的管理 Tab
    getTabFromSearch = (search: string): TabKey | null => {
        const tab = new URLSearchParams(search).get('tab');
        return tabs.some(item => item.key === tab) ? tab as TabKey : null;
    };

    // 一级 Tab 和奖品分类共用 window 滚动；切换后统一回到页面顶部。
    resetPageScroll = () => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        window.scrollTo(0, 0);
        // 兼容部分旧 WebView 使用 body 作为滚动根节点的情况。
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (this.state.scrollY !== 0 || this.state.scrollDirection !== null) {
            this.setState({ scrollY: 0, scrollDirection: null });
        }
    };

    // 切换管理 Tab 时同步路由，并退出地址选择模式
    handleTabChange = (item: { key: TabKey }) => {
        if (this.isSelectAddressMode(this.props.location.search)) return;
        const { history, location } = this.props;
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('tab', item.key);
        searchParams.delete('select');
        searchParams.delete('goodsCode');
        searchParams.delete('orderId');
        searchParams.delete('winningId');
        const search = searchParams.toString();

        this.setState({ activeTab: item.key, selectedAddress: null, addressConfirmError: '' });
        history.replace({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
            hash: location.hash,
        });
    };

    // 切换我的奖品二级分类，并将分类 type 同步到路由和接口请求。
    handlePrizeTypeChange = (type: number) => {
        if (this.isSelectAddressMode(this.props.location.search) || this.state.prizeFilterType === type) return;
        const { history, location } = this.props;
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('tab', 'prizes');
        searchParams.set('type', String(type));
        history.replace({
            pathname: location.pathname,
            search: `?${searchParams.toString()}`,
            hash: location.hash,
        });
        this.setState({ prizeFilterType: type }, () => {
            this.resetPageScroll();
            this.loadPrizes();
        });
    };

    // 从我的奖品进入收货地址选择模式，并透传中奖记录 ID。
    openAddressSelectionFromPrize = (prize: any) => {
        const { history } = this.props;
        const winningRecordId = getWinningRecordId(prize);
        const searchParams = new URLSearchParams({ tab: 'address', select: '1' });
        if (winningRecordId) searchParams.set('winningId', String(winningRecordId));
        this.setState({ activeTab: 'address' }, () => {
            history.push(`/prizeManagement?${searchParams.toString()}`);
        });
    };

    // 根据当前模式更新页面标题
    updateTitle = () => {
        document.title = this.isSelectAddressMode(this.props.location.search) ? '收货地址' : '奖品管理';
    };

    // 监听奖品或兑换记录列表滚动到底部，触发下一页加载。
    handlePrizeListScroll = () => {
        // 单独同步滚动位置，确保回到顶部时及时移除悬浮样式。
        const scrollTop = getPageScrollTop();
        if (scrollTop !== this.state.scrollY) this.setState({ scrollY: scrollTop });
        if (!['prizes', 'records'].includes(this.state.activeTab) || this.isSelectAddressMode(this.props.location.search)) return;

        const scrollHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        const distanceToBottom = scrollHeight - scrollTop - window.innerHeight;
        if (distanceToBottom > 240) return;
        if (this.state.activeTab === 'prizes') this.loadNextPrizes();
        if (this.state.activeTab === 'records') this.loadNextRecords();
    };

    // 按分页元数据加载奖品列表下一页，并追加到当前列表末尾。
    loadNextPrizes = () => {
        const { prizesPage, prizesHasMore, prizesLoadingMore } = this.state;
        if (!prizesHasMore || prizesLoadingMore || this.prizesRequesting) return;
        this.loadPrizes(prizesPage + 1, true);
    };

    // 按分页元数据加载兑换记录下一页，并追加到当前列表末尾。
    loadNextRecords = () => {
        const { recordsPage, recordsHasMore, recordsLoadingMore } = this.state;
        if (!recordsHasMore || recordsLoadingMore || this.recordsRequesting) return;
        this.loadRecords(recordsPage + 1, true);
    };

    // 读取列表接口返回的 hasMore 标记，兼容布尔值和数字字符串。
    getListHasMore = (data: any) => {
        const hasMore = data?.page?.hasMore ?? data?.page?.has_more;
        return hasMore === true || hasMore === 1 || hasMore === '1' || hasMore === 'true';
    };

    // 加载我的奖品列表
    loadPrizes = async (pageNumber = 1, append = false) => {
        if (this.isSelectAddressMode(this.props.location.search)) {
            if (!append) this.setState({ loading: false });
            return;
        }
        if (this.prizesRequesting || (append && !this.state.prizesHasMore)) return;

        this.prizesRequesting = true;
        const requestType = this.state.prizeFilterType;
        if (append) {
            this.setState({ prizesLoadingMore: true });
        } else {
            this.setState({
                loading: true,
                error: '',
                prizes: [],
                prizesPage: 0,
                prizesHasMore: false,
                prizesLoadingMore: false,
            });
        }

        try {
            const res = await getMyPrizeWinningList({
                'page[number]': pageNumber,
                'page[size]': PAGE_SIZE,
                type: requestType,
            });
            if (!this.mounted) return;
            if (requestType !== this.state.prizeFilterType) return;
            if (res.code === 0) {
                const pageData = getPrizeList(res.data);
                this.setState(prevState => ({
                    prizes: append ? [...prevState.prizes, ...pageData] : pageData,
                    loading: false,
                    prizesPage: pageNumber,
                    prizesHasMore: this.getListHasMore(res.data),
                    prizesLoadingMore: false,
                }));
            } else if (append) {
                this.setState({ prizesLoadingMore: false });
            } else {
                this.setState({ error: res.msg || '奖品列表加载失败', loading: false });
            }
        } catch (_) {
            if (this.mounted) {
                if (append) {
                    this.setState({ prizesLoadingMore: false });
                } else {
                    this.setState({ error: '奖品列表加载失败，请稍后重试', loading: false });
                }
            }
        } finally {
            this.prizesRequesting = false;
            if (this.mounted && requestType !== this.state.prizeFilterType) this.loadPrizes();
        }
    };

    // 按当前 Tab 加载兑换记录或收货地址
    loadActiveTabData = () => {
        if (this.state.activeTab === 'records') this.loadRecords();
        if (this.state.activeTab === 'address') this.loadAddresses();
    };

    // 加载兑换记录列表，支持首次加载和滚动追加下一页。
    loadRecords = async (pageNumber = 1, append = false) => {
        if (this.recordsRequesting || (append && !this.state.recordsHasMore)) return;
        if (!append && this.state.recordsLoaded) return;

        this.recordsRequesting = true;
        if (append) {
            this.setState({ recordsLoadingMore: true });
        } else {
            this.setState({
                records: [],
                recordsLoading: true,
                recordsLoaded: false,
                recordsPage: 0,
                recordsHasMore: false,
                recordsLoadingMore: false,
                recordsError: '',
            });
        }

        try {
            const res = await getPrizeExchangeList({
                'page[number]': pageNumber,
                'page[size]': PAGE_SIZE,
            });
            if (!this.mounted) return;
            if (res.code === 0) {
                const pageData = getPrizeList(res.data);
                this.setState(prevState => ({
                    records: append ? [...prevState.records, ...pageData] : pageData,
                    recordsLoading: false,
                    recordsLoaded: true,
                    recordsPage: pageNumber,
                    recordsHasMore: this.getListHasMore(res.data),
                    recordsLoadingMore: false,
                }));
            } else if (append) {
                this.setState({ recordsLoadingMore: false });
            } else {
                this.setState({ recordsError: res.msg || '兑换记录加载失败', recordsLoading: false });
            }
        } catch (_) {
            if (this.mounted) {
                if (append) {
                    this.setState({ recordsLoadingMore: false });
                } else {
                    this.setState({ recordsError: '兑换记录加载失败，请稍后重试', recordsLoading: false });
                }
            }
        } finally {
            this.recordsRequesting = false;
        }
    };

    // 加载收货地址列表
    loadAddresses = async () => {
        if (this.state.addressesLoaded || this.state.addressesLoading) return;
        this.setState({ addressesLoading: true, addressesError: '' });
        try {
            const res = await getUserAddressList({ page: 1, perPage: PAGE_SIZE, default: 1 });
            if (!this.mounted) return;
            if (res.code === 0) {
                this.setState({ addresses: getPrizeList(res.data), addressesLoaded: true, addressesLoading: false });
            } else {
                this.setState({ addressesError: res.msg || '收货地址加载失败', addressesLoading: false });
            }
        } catch (_) {
            if (this.mounted) this.setState({ addressesError: '收货地址加载失败，请稍后重试', addressesLoading: false });
        }
    };

    // 打开删除收货地址的二次确认弹框。
    openDeleteAddressConfirm = (address: any) => {
        this.setState({ addressToDelete: address, addressDeleteError: '' });
    };

    // 取消删除收货地址并关闭确认弹框。
    cancelDeleteAddress = () => {
        if (this.state.addressDeleting) return;
        this.setState({ addressToDelete: null, addressDeleteError: '' });
    };

    // 调用删除接口，成功后从当前地址列表移除已删除地址。
    confirmDeleteAddress = async () => {
        const { addressToDelete, addressDeleting } = this.state;
        if (!addressToDelete || addressDeleting) return;

        const addressId = valueOf(addressToDelete, 'id', 'addressId', 'address_id');
        const userId = this.props.user?.userInfo?.id ?? this.props.user?.userInfo?.userId;
        if (!addressId) {
            this.setState({ addressDeleteError: '收货地址信息缺失，请刷新后重试' });
            return;
        }

        this.setState({ addressDeleting: true, addressDeleteError: '' });
        try {
            const res = await deleteUserAddress({
                ...(userId ? { userId } : {}),
                id: addressId,
            });
            if (!this.mounted) return;
            if (res.code === 0) {
                this.setState(prevState => ({
                    addresses: prevState.addresses.filter(item => String(valueOf(item, 'id', 'addressId', 'address_id')) !== String(addressId)),
                    addressToDelete: null,
                    addressDeleting: false,
                }));
                // 删除地址成功后提示用户，并关闭确认弹框。
                Toast.success({ content: '删除成功' });
            } else {
                this.setState({ addressDeleting: false, addressDeleteError: res.msg || '删除收货地址失败，请稍后重试' });
            }
        } catch (_) {
            if (this.mounted) this.setState({ addressDeleting: false, addressDeleteError: '删除收货地址失败，请稍后重试' });
        }
    };

    // 设置默认收货地址，成功后同步更新当前列表中的默认状态。
    setDefaultAddress = async (address: any) => {
        const addressId = valueOf(address, 'id', 'addressId', 'address_id');
        if (!addressId || this.state.settingDefaultId !== null) return;
        if (Number(valueOf(address, 'is_default', 'isDefault', 'default')) !== 0) return;

        this.setState({ settingDefaultId: addressId, addressesError: '' });
        try {
            const res = await setDefaultUserAddress({ id: addressId });
            if (!this.mounted) return;
            if (res.code === 0) {
                this.setState(prevState => ({
                    addresses: prevState.addresses.map(item => ({
                        ...item,
                        is_default: String(valueOf(item, 'id', 'addressId', 'address_id')) === String(addressId) ? 1 : 0,
                    })),
                }));
            } else {
                this.setState({ addressesError: res.msg || '设置默认地址失败，请稍后重试' });
            }
        } catch (_) {
            if (this.mounted) this.setState({ addressesError: '设置默认地址失败，请稍后重试' });
        } finally {
            if (this.mounted) this.setState({ settingDefaultId: null });
        }
    };

    // 二次确认后修改订单收货地址，接口成功才返回我的奖品列表。
    confirmAddressSelection = async () => {
        const { history, location, goods } = this.props;
        const { selectedAddress, addressConfirming } = this.state;
        if (!selectedAddress || addressConfirming) return;

        const searchParams = new URLSearchParams(location.search);
        const goodsCode = searchParams.get('goodsCode');
        const winningRecordId = searchParams.get('winningId');
        const addressId = valueOf(selectedAddress, 'id', 'addressId', 'address_id');

        // 商品详情兑换场景只回传地址到商品详情页，不修改已有订单。
        if (goodsCode) {
            goods?.selectGoodsAddress(selectedAddress);
            this.setState({ selectedAddress: null, addressConfirmError: '' });
            history.replace(`/goodsDetail/${encodeURIComponent(goodsCode)}`, { openExchange: true });
            return;
        }

        if (!winningRecordId || !addressId) {
            this.setState({ addressConfirmError: '中奖记录或收货地址信息缺失，请重新选择' });
            return;
        }

        this.setState({ addressConfirming: true, addressConfirmError: '' });
        try {
            const res = await updateOrderAddress({ id: winningRecordId, addressId });
            if (!this.mounted) return;
            if (res.code === 0) {
                this.setState({
                    selectedAddress: null,
                    addressConfirming: false,
                }, () => {
                    // 修改订单地址成功后，清理选择参数并回到我的奖品列表。
                    history.replace('/prizeManagement?tab=prizes');
                });
                return;
            }
            this.setState({
                addressConfirming: false,
                addressConfirmError: res.msg || '修改订单地址失败，请稍后重试',
            });
        } catch (_) {
            if (this.mounted) {
                this.setState({
                    addressConfirming: false,
                    addressConfirmError: '修改订单地址失败，请稍后重试',
                });
            }
        }
    };

    // 渲染中奖奖品列表及发放状态
    renderPrizes = () => {
        const { prizes, loading, error, prizesLoadingMore } = this.state;
        if (loading) return <div className={styles.pageState}>奖品加载中...</div>;
        if (error) return <div className={styles.pageState}>{error}</div>;
        if (!prizes.length) return <EmptyState text="你还没有中奖" />;

        return <div className={styles.list}>{prizes.map((item, index) => {
            const name = valueOf(item, 'prize_name', 'prizeName', 'name', 'title', 'award') || '奖品';
            const icon = valueOf(item, 'prize_pic', 'prizeIcon', 'icon', 'cover', 'prizeCover', 'image');
            const winTime = valueOf(item, 'winningTime', 'winningAt', 'createdAt', 'created_at', 'winTime');
            const prizeType = Number(valueOf(item, 'prize_type', 'prizeType'));
            const prizeCategory = String(valueOf(
                item,
                'prize_category',
                'prizeCategory',
                'category',
                'categoryName',
                'typeName',
            )).toLowerCase();
            // 公益奖品不涉及领取或发货状态；兼容类型值、语义字段及旧数据名称。
            const isCharityPrize = prizeType === 5
                || Number(valueOf(item, 'is_charity', 'isCharity', 'is_welfare', 'isWelfare')) === 1
                || /公益|charity|welfare/.test(prizeCategory)
                || String(name).includes('公益');
            const receivedStatus = Number(valueOf(item, 'is_received', 'isReceived'));
            const orderInfo = item.order || item.orderInfo || item.order_info || {};
            const receivingInfo = item.receiving || item.receivingInfo || item.receiving_info || {};
            const logisticsInfo = item.logisticsInfo || item.logistics || item.logistics_info || {};
            const cardInfo = item.card || item.cardInfo || item.card_info || item.coupon || item.couponInfo || item.coupon_info || {};
            const isShipped = Boolean(item.sendAt || item.send_at || logisticsInfo?.isShipped || logisticsInfo?.is_shipped);
            const inferredStatus = prizeType !== 2
                ? '已发放'
                : receivedStatus === 2
                    ? '已过期'
                    // 暂不展示“填写地址”状态，待产品确认后再恢复
                    : isShipped ? '已发货' : '待发货';
            const statusText = valueOf(item, 'statusText', 'statusName', 'stateText') || inferredStatus;
            const statusStyle = statusText.includes('过期') || statusText.includes('取消') ? styles.expired : statusText.includes('发放') || statusText.includes('完成') ? styles.done : styles.pending;
            const logisticsNo = valueOf(item, 'logisticsNo', 'logistics_no', 'expressNo', 'express_no', 'trackingNo', 'tracking_no')
                || valueOf(logisticsInfo, 'logisticsNo', 'logistics_no', 'expressNo', 'express_no', 'trackingNo', 'tracking_no');
            const logisticsCompany = valueOf(item, 'logisticsCompany', 'logistics_company', 'expressCompany', 'express_company')
                || valueOf(logisticsInfo, 'logisticsCompany', 'logistics_company', 'expressCompany', 'express_company');
            const cardNo = valueOf(item, 'cardNo', 'cardNumber', 'card_no', 'code')
                || valueOf(cardInfo, 'cardNo', 'cardNumber', 'card_no', 'couponNo', 'coupon_no', 'code');
            const cardPassword = valueOf(item, 'cardPassword', 'cardPwd', 'card_password', 'password')
                || valueOf(cardInfo, 'cardPassword', 'cardPwd', 'card_password', 'couponSecret', 'coupon_secret', 'password');
            const orderNo = valueOf(item, 'orderNo', 'orderNumber', 'order_no')
                || valueOf(orderInfo, 'orderNo', 'orderNumber', 'order_no', 'no');
            const receiver = valueOf(item, 'receiver', 'receiverName', 'consignee')
                || valueOf(receivingInfo, 'trueName', 'true_name', 'receiver', 'receiverName', 'consignee', 'name');
            const phone = valueOf(item, 'phone', 'mobile', 'receiverPhone')
                || valueOf(receivingInfo, 'phone', 'mobile', 'receiverPhone');
            const address = valueOf(item, 'address', 'receiverAddress', 'fullAddress')
                || [
                    valueOf(receivingInfo, 'province', 'provinceName'),
                    valueOf(receivingInfo, 'city', 'cityName'),
                    valueOf(receivingInfo, 'area', 'district', 'districtName', 'areaName'),
                    valueOf(receivingInfo, 'detailAddress', 'detail_address', 'addressDetail'),
                ].filter(Boolean).join('');
            // 备注兼容奖品、优惠券及订单对象中的不同字段命名。
            const remark = valueOf(item, 'remark', 'notes', 'description', 'coupon_note', 'couponNote')
                || valueOf(cardInfo, 'coupon_note', 'couponNote', 'note', 'remark', 'notes', 'description')
                || valueOf(orderInfo, 'remark', 'notes', 'description');
            // 实物奖品未填写收货信息、优惠券奖品时，不展示订单号。
            const hasReceivingInfo = Boolean(receiver || phone || address);
            const displayOrderNo = prizeType === 3 || (prizeType === 2 && !hasReceivingInfo) ? '' : orderNo;
            const needsAddress = prizeType === 2 && receivedStatus === 0;

            return <article className={styles.card} key={valueOf(item, 'id', 'winningId', 'prizeId') || index}>
                {/* 奖品基本信息和当前发放状态 */}
                <div className={styles.cardHeader}>
                    {icon ? <img className={styles.prizeIcon} src={icon} alt="" /> : <WatchIcon />}
                    <div className={styles.heading}>
                        <h2 className={styles.headingTitle}>{name}</h2>
                        <p className={styles.headingTime}>中奖时间：{winTime ? formatDateTime(winTime) : '--'}</p>
                    </div>
                    {!isCharityPrize && <span className={`${styles.status} ${statusStyle}`}>{statusText}</span>}
                </div>
                {/* 卡号、卡密、订单及收货信息详情；优惠券类型不展示订单号 */}
                {(logisticsNo || cardNo || cardPassword || displayOrderNo || receiver || address || remark) && <div className={styles.details}>
                    {logisticsNo && <div className={styles.detailRow}><span className={styles.detailLabel}>物流单号：</span><div className={styles.detailValue}>{logisticsCompany && `${logisticsCompany} `}{logisticsNo} <CopyButton value={String(logisticsNo)} /></div></div>}
                    {cardNo && <div className={styles.detailRow}><span className={styles.detailLabel}>卡号：</span><div className={styles.detailValue}>{cardNo} <CopyButton value={String(cardNo)} /></div></div>}
                    {cardPassword && <div className={styles.detailRow}><span className={styles.detailLabel}>卡密：</span><div className={styles.detailValue}>{cardPassword} <CopyButton value={String(cardPassword)} /></div></div>}
                    {displayOrderNo && <div className={styles.detailRow}><span className={styles.detailLabel}>订单号：</span><div className={styles.detailValue}>{displayOrderNo} <CopyButton value={String(displayOrderNo)} /></div></div>}
                    {receiver && <div className={styles.detailRow}><span className={styles.detailLabel}>收货人：</span><div className={styles.detailValue}>{receiver} {phone}</div></div>}
                    {address && <div className={styles.detailRow}><span className={styles.detailLabel}>收货地址：</span><div className={styles.detailValue}>{address}</div></div>}
                    {remark && <div className={styles.detailRow}><span className={styles.detailLabel}>备注：</span><div className={styles.detailValue}>{remark}</div></div>}
                </div>}
                {/* 实物奖品待补充收货地址时显示选择按钮 */}
                {needsAddress && <button className={styles.addressButton} onClick={() => this.openAddressSelectionFromPrize(item)}><img src={`${BASE_IMG_URL}/goods/icon3.png`} alt="" className={styles.addressButtonIcon} />选择收货地址</button>}
            </article>;
            })}
            {prizesLoadingMore && <div className={styles.loadMoreState}>加载更多...</div>}
        </div>;
    };

    // 渲染兑换记录及物流、卡密信息
    renderRecords = () => {
        const { records, recordsLoading, recordsLoaded, recordsLoadingMore, recordsError } = this.state;
        if (recordsLoading) return <div className={styles.pageState}>兑换记录加载中...</div>;
        if (recordsError) return <div className={styles.pageState}>{recordsError}</div>;
        if (recordsLoaded && !records.length) return <EmptyState text="你还没有兑换过" />;

        return <div className={styles.list}>{records.map((item, index) => {
            const goods = item.goods || {};
            const goodsInfo = item.goods_info || {};
            const coupon = goodsInfo.coupon || {};
            const logistics = item.logistics_info || {};
            const receiving = item.receiving_info || {};
            const goodsType = Number(item.goods_type);
            const isRefund = Number(item.is_refund) === 1;
            const isLottery = Boolean(item.is_lottery);
            const isShipped = Boolean(item.send_at);
            let inferredStatus = '处理中';
            if (goodsType === 1) {
                if (isLottery) {
                    if (Number(item.lottery_status) === 2) inferredStatus = '未中奖';
                    else if (Number(item.lottery_status) === 0) inferredStatus = '待开奖';
                    else if (isRefund) inferredStatus = '已退货';
                    else inferredStatus = isShipped ? '已发货' : '待发货';
                } else if (isRefund) inferredStatus = '已退货';
                else inferredStatus = isShipped ? '已发货' : '待发货';
            } else if (goodsType === 2) {
                inferredStatus = '兑换成功';
            } else if (goodsType === 3) {
                if (Number(item.audit_status) === 0) inferredStatus = '待审核';
                else if (Number(item.audit_status) === 2) inferredStatus = '审核不通过';
                else if (isRefund) inferredStatus = '已退货';
                else if (Number(goods.is_need_address) === 1) inferredStatus = Number(item.status) === 1 ? '已发货' : '待发货';
                else inferredStatus = Number(item.send_user_id) !== 0 ? '已发货' : '审核通过';
            }
            const name = goodsInfo.goodsName || '兑换奖品';
            const icon = item.goodsCover;
            const statusText = valueOf(item, 'statusText', 'statusName', 'stateText') || inferredStatus;
            const amount = item.score;
            const showAmount = ![3, 6, 7].includes(Number(item.order_source));
            const cardNo = coupon.coupon_no;
            const cardPassword = coupon.coupon_secret;
            const description = coupon.coupon_note;
            const logisticsNo = logistics.expressNo;
            const logisticsCompany = logistics.expressCompany;
            const orderNo = item.order_no;
            const receiver = receiving.trueName;
            const phone = receiving.phone;
            const address = receiver ? `${receiving.province || ''}${receiving.city || ''}${receiving.area || ''}${receiving.detailAddress || ''}` : '';
            const imei = item.form_info?.imei;
            const statusStyle = statusText.includes('退货') || statusText.includes('不通过') || statusText.includes('未中奖')
                ? styles.expired
                : statusText.includes('成功') || statusText.includes('已发货') || statusText.includes('通过')
                    ? styles.done
                    : styles.pending;

            return <article className={styles.recordCard} key={valueOf(item, 'id', 'exchangeId', 'orderId') || index}>
                {/* 兑换商品、积分和处理状态 */}
                <div className={styles.recordHeader}>
                    {icon ? <img className={styles.recordIcon} src={icon} alt="" /> : <div className={`${styles.recordIcon} ${styles.memberIcon}`}>V</div>}
                    <div className={styles.recordHeading}>
                        <h2 className={styles.recordTitle}>{name}</h2>
                        {showAmount && <strong className={styles.recordScore}><img src={`${BASE_IMG_URL}/goods/icon2.png`} alt="" className={styles.recordCoinIcon} />{amount || 0}</strong>}
                    </div>
                    <span className={`${styles.status} ${statusStyle}`}>{statusText}</span>
                </div>
                {/* 物流、卡密、订单及收货详情 */}
                {(cardNo || cardPassword || logisticsNo || orderNo || receiver || address || imei || description) && <div className={styles.recordDetails}>
                    {logisticsNo && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>物流单号：</span><div className={styles.recordDetailValue}>{logisticsCompany && `${logisticsCompany} `}{logisticsNo} <CopyButton value={String(logisticsNo)} /></div></div>}
                    {cardNo && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>卡号：</span><div className={styles.recordDetailValue}>{cardNo} <CopyButton value={String(cardNo)} /></div></div>}
                    {cardPassword && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>卡密：</span><div className={styles.recordDetailValue}>{cardPassword} <CopyButton value={String(cardPassword)} /></div></div>}
                    {orderNo && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>订单号：</span><div className={styles.recordDetailValue}>{orderNo} <CopyButton value={String(orderNo)} /></div></div>}
                    {receiver && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>收货人：</span><div className={styles.recordDetailValue}>{receiver} {phone}</div></div>}
                    {address && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>收货地址：</span><div className={styles.recordDetailValue}>{address}</div></div>}
                    {imei && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>IMEI：</span><div className={styles.recordDetailValue}>{imei}</div></div>}
                    {description && <div className={styles.recordDetailRow}><span className={styles.recordDetailLabel}>备注：</span><div className={styles.recordDetailValue}>{description}</div></div>}
                </div>}
            </article>;
            })}
            {recordsLoadingMore && <div className={styles.loadMoreState}>加载更多...</div>}
        </div>;
    };

    // 渲染收货地址列表及操作按钮
    renderAddress = () => {
        const { addresses, addressesLoading, addressesLoaded, addressesError, settingDefaultId } = this.state;
        const selectAddressMode = this.isSelectAddressMode(this.props.location.search);
        const { history } = this.props;
        if (addressesLoading) return <div className={styles.pageState}>收货地址加载中...</div>;
        if (addressesError) return <div className={styles.pageState}>{addressesError}</div>;

        return <div className={styles.addressList}>
            {addressesLoaded && !addresses.length && <EmptyState text="你还没有收货地址" />}
            {addresses.map((item, index) => {
                const addressId = valueOf(item, 'id', 'addressId', 'address_id');
                const id = addressId || index;
                const receiver = valueOf(item, 'true_name', 'name', 'receiver', 'receiverName', 'consignee');
                const phone = valueOf(item, 'phone', 'mobile', 'receiverPhone');
                const region = [valueOf(item, 'province', 'provinceName'), valueOf(item, 'city', 'cityName'), valueOf(item, 'district', 'districtName', 'area')].filter(Boolean).join(' ');
                const detail = valueOf(item, 'detail_address', 'address', 'detail', 'detailAddress', 'addressDetail', 'fullAddress');
                const isDefault = Number(valueOf(item, 'is_default', 'isDefault', 'default')) !== 0;

                return <article
                    key={id}
                    className={`${styles.addressPanel} ${selectAddressMode ? styles.selectableAddress : ''}`}
                    onClick={selectAddressMode ? () => this.setState({ selectedAddress: item, addressConfirmError: '' }) : undefined}
                >
                    {/* 收货人、电话和详细地址 */}
                    <h2 className={styles.addressTitle}>{receiver || '收货人'} <b className={styles.addressPhone}>{phone}</b></h2>
                    <p className={styles.addressText}>{region} {detail}</p>
                    {/* 默认地址标记与编辑、删除操作 */}
                    <div className={styles.addressActions}>
                        <button
                            type="button"
                            className={styles.defaultCheck}
                            disabled={settingDefaultId !== null}
                            onClick={event => {
                                event.stopPropagation();
                                this.setDefaultAddress(item);
                            }}
                        >
                            <img
                                className={styles.defaultCheckIcon}
                                src={`${BASE_IMG_URL}/common/setPost/${isDefault ? 'at-checked-active3.png' : 'at-checkout3.png'}`}
                                alt=""
                            />
                            默认发货地址
                        </button>
                        <div className={styles.addressActionButtons}>
                            <button
                                type="button"
                                className={styles.deleteButton}
                                onClick={event => {
                                    event.stopPropagation();
                                    this.openDeleteAddressConfirm(item);
                                }}
                            >删除</button>
                            <button type="button" className={styles.editButton} onClick={event => {
                                event.stopPropagation();
                                history.push('/addressForm', { address: item });
                            }}>编辑</button>
                        </div>
                    </div>
                </article>;
            })}
            <div className={styles.addAddressBar}>
                <button className={styles.addAddressButton} type="button" onClick={() => history.push('/addressForm')}>新建收货地址</button>
            </div>
        </div>;
    };

    // 渲染管理导航、当前内容和地址确认弹窗；地址选择模式隐藏导航。
    render() {
        const { site } = this.props;
        const {
            activeTab,
            prizeFilterType,
            selectedAddress,
            addressConfirming,
            addressConfirmError,
            addressToDelete,
            addressDeleting,
            addressDeleteError,
            scrollY,
            scrollDirection,
        } = this.state;
        const selectAddressMode = this.isSelectAddressMode(this.props.location.search);
        // 页面滚动离开顶部后保持分类导航的悬浮样式，上下滚动均生效。
        const isPrizeTypeTabsFloating = scrollY > 0;
        const activeTabIndex = Math.max(tabs.findIndex(item => item.key === activeTab), 0);

        return (
        <div className={classNames(styles.page, this.props.desktopMode && styles.desktopMode)}>
            {!selectAddressMode && <>
                {/* 奖品管理 Tab 导航 */}
                <div
                    className={styles.tabs}
                    style={{
                        top: getUrlParams('source') === 'app'
                            ? 0
                            : px2rem(
                                (site?.appDownloadShowStatus ? 88 : 0)
                                + (site?.showHeader !== false && scrollDirection !== 'down' ? 88 : 0)
                            ),
                    }}
                >
                    <TabNav variant="h5"
                        navList={tabs}
                        currentIndex={activeTabIndex}
                        navClick={this.handleTabChange}
                        boxStylesData={{ background: '#fff' }}
                    />
                    {activeTab === 'prizes' && <div className={classNames(styles.prizeTypeTabs, isPrizeTypeTabsFloating && styles.prizeTypeTabsFloating)}>
                        {/* 我的奖品分类筛选，固定在一级 Tab 导航下方。 */}
                        {prizeTypeTabs.map(item => (
                            <button
                                key={item.type}
                                type="button"
                                className={classNames(styles.prizeTypeTab, prizeFilterType === item.type && styles.prizeTypeTabActive)}
                                aria-pressed={prizeFilterType === item.type}
                                onClick={() => this.handlePrizeTypeChange(item.type)}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>}
                </div>
            </>}
            {/* 当前 Tab 对应的奖品、记录或地址内容 */}
            {activeTab === 'prizes' && this.renderPrizes()}
            {activeTab === 'records' && this.renderRecords()}
            {activeTab === 'address' && this.renderAddress()}
            {/* 选择收货地址确认弹窗 */}
            <CommonPopupNew variant="h5"
                visible={Boolean(selectedAddress)}
                title="温馨提示"
                confirmText={addressConfirming ? '提交中...' : '确定'}
                cancelText="取消"
                confirmDisabled={addressConfirming}
                onCancelChange={() => {
                    if (!addressConfirming) this.setState({ selectedAddress: null, addressConfirmError: '' });
                }}
                onConfirmButtonChange={this.confirmAddressSelection}
            >
                <div className={styles.addressConfirmText}>确定选择此地址吗？</div>
                {addressConfirmError && <div className={styles.addressConfirmError}>{addressConfirmError}</div>}
            </CommonPopupNew>
            {/* 删除收货地址二次确认弹窗 */}
            <CommonPopupNew variant="h5"
                visible={Boolean(addressToDelete)}
                title="温馨提示"
                confirmText={addressDeleting ? '删除中...' : '删除'}
                cancelText="取消"
                confirmDisabled={addressDeleting}
                onCancelChange={this.cancelDeleteAddress}
                onConfirmButtonChange={this.confirmDeleteAddress}
            >
                <div className={styles.addressConfirmText}>是否删除收货地址？</div>
                {addressDeleteError && <div className={styles.addressConfirmError}>{addressDeleteError}</div>}
            </CommonPopupNew>
        </div>
        );
    }
}

export default inject('goods', 'site', 'user')(observer(withRouter(PrizeManagementH5)));
